import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Chip, Grid, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData, SquareService } from '../../api';

export const SquareDisconnectedIndicator: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const isSquareDisconnected = !!account?.square_connect?.connection_lost_at;

  const { data: squareStatus } = useSWR(
    isSquareDisconnected ? 'squareStatus' : null,
    () => SquareService.getSquareStatus(),
    {
      refreshInterval: 300000,
    },
  );

  if (!isSquareDisconnected) {
    return null;
  }

  const stoppedRenewalsCount = squareStatus?.renewals_waiting_count ?? 0;

  const handleClick = () => {
    navigate('/dashboard/payments');
  };

  return (
    <Tooltip title={t('calendar.squareDisconnected.tooltip')} arrow>
      <Grid onClick={handleClick} sx={{ cursor: 'pointer' }}>
        <Chip
          icon={<LinkOffIcon />}
          label={
            <Grid sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                {t('calendar.squareDisconnected.label')}
              </Typography>
              {stoppedRenewalsCount > 0 && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8, lineHeight: 1.1 }}>
                  {t('calendar.squareDisconnected.sub', { count: stoppedRenewalsCount })}
                </Typography>
              )}
            </Grid>
          }
          variant="outlined"
          size="small"
          sx={{
            height: 'auto',
            minHeight: stoppedRenewalsCount > 0 ? 48 : 32,
            borderColor: 'error.main',
            color: 'error.main',
            '& .MuiChip-icon': {
              color: 'error.main',
            },
            '& .MuiChip-label': {
              padding: '6px 8px',
            },
            '&:hover': {
              backgroundColor: 'error.light',
              color: 'error.main',
              '& .MuiChip-icon': {
                color: 'error.main',
              },
            },
          }}
        />
      </Grid>
    </Tooltip>
  );
};
